import { useCallback, useMemo } from 'react';
import { useEnrichedAuth } from './useEnrichedAuth';
import { DOMAINS, DomainKey, DomainConfig } from '../config/domains';

/**
 * Check if user has access to a domain based on role/permission requirements
 */
function checkDomainAccess(
  domain: DomainConfig,
  userRoles: string[],
  userPermissions: string[]
): boolean {
  // If no requirements specified, domain is accessible to all authenticated users
  if (!domain.requiredRoles && !domain.requiredPermissions) {
    return true;
  }

  const requireAll = domain.requireAll || false;
  const strictAccess = domain.strictAccess || false;

  // Admin users have access to everything (unless strictAccess is enabled)
  if (!strictAccess && (userRoles.includes('Admin') || userRoles.includes('admin'))) {
    return true;
  }

  // Check role requirements
  if (domain.requiredRoles && domain.requiredRoles.length > 0) {
    const hasRequiredRole = requireAll
      ? domain.requiredRoles.every((role) => userRoles.includes(role))
      : domain.requiredRoles.some((role) => userRoles.includes(role));

    // If only roles are specified, return based on role check
    if (!domain.requiredPermissions || domain.requiredPermissions.length === 0) {
      return hasRequiredRole;
    }

    // If both roles and permissions are specified with requireAll, must pass both
    if (requireAll && !hasRequiredRole) {
      return false;
    }

    // If both specified with requireAll=false, passing either is enough
    if (!requireAll && hasRequiredRole) {
      return true;
    }
  }

  // Check permission requirements
  if (domain.requiredPermissions && domain.requiredPermissions.length > 0) {
    const hasRequiredPermission = requireAll
      ? domain.requiredPermissions.every((perm) => userPermissions.includes(perm))
      : domain.requiredPermissions.some((perm) => userPermissions.includes(perm));

    return hasRequiredPermission;
  }

  return false;
}

/**
 * Hook for domain access control
 * Provides reusable methods for checking user's domain permissions based on roles and rights
 */
export function useDomainAccess() {
  const { user } = useEnrichedAuth();

  const userRoles = user?.roles || [];
  const userPermissions = user?.permissions || [];

  /**
   * Check if user can access a specific domain based on role/permission requirements
   */
  const canAccessDomain = useCallback(
    (domainKey: DomainKey): boolean => {
      const domain = DOMAINS[domainKey];
      if (!domain) return false;

      return checkDomainAccess(domain, userRoles, userPermissions);
    },
    [userRoles, userPermissions]
  );

  /**
   * Get all domains the user has access to
   */
  const getAccessibleDomains = useCallback((): DomainConfig[] => {
    return Object.values(DOMAINS).filter(
      (domain) => !domain.hidden && checkDomainAccess(domain, userRoles, userPermissions)
    );
  }, [userRoles, userPermissions]);

  /**
   * Get list of accessible domain keys
   */
  const getAccessibleDomainKeys = useCallback((): DomainKey[] => {
    return Object.keys(DOMAINS).filter(
      (key) => !DOMAINS[key as DomainKey]?.hidden && canAccessDomain(key as DomainKey)
    ) as DomainKey[];
  }, [canAccessDomain]);

  /**
   * Get accessible domains as a memoized value
   */
  const accessibleDomains = useMemo(() => {
    return getAccessibleDomains();
  }, [getAccessibleDomains]);

  /**
   * Get accessible domain keys as a memoized value
   */
  const accessibleDomainKeys = useMemo(() => {
    return getAccessibleDomainKeys();
  }, [getAccessibleDomainKeys]);

  /**
   * Check if user has access to any domains
   */
  const hasAnyDomainAccess = useMemo(() => {
    return accessibleDomains.length > 0;
  }, [accessibleDomains]);

  return {
    // Methods
    getAccessibleDomains,
    canAccessDomain,
    getAccessibleDomainKeys,

    // Memoized values
    accessibleDomains,
    accessibleDomainKeys,
    hasAnyDomainAccess,
  };
}
